import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { ensureAuthToken } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2, ChevronRight, Check, Target, Search as SearchIcon } from "lucide-react";

const Step = ({ n, title, active, done }) => (
  <div className="flex items-center gap-3">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
      ${done ? "bg-[#2C4C3B] text-white" : active ? "bg-[#E6F0E9] text-[#2C4C3B] border border-[#2C4C3B]" : "bg-[#F4F5F0] text-[#52606D]"}`}>
      {done ? <Check className="w-4 h-4" /> : n}
    </div>
    <span className={`text-sm hidden sm:inline ${active || done ? "text-[#2C4C3B] font-medium" : "text-[#52606D]"}`}>{title}</span>
  </div>
);

const isArticulationDifference = (d) => d?.slug === "articulation-disorder";
const isGlpDifference = (d) => d?.slug === "gestalt-language-processing";

export default function Goals() {
  const { authHeaders, user, openAuth, refreshMe } = useAuth();
  const nav = useNavigate();
  const [activities, setActivities] = useState([]);
  const [differences, setDifferences] = useState([]);
  const [communicators, setCommunicators] = useState([]);
  const [severities, setSeverities] = useState([]);

  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [customActivity, setCustomActivity] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [activityFilter, setActivityFilter] = useState("");
  const [ageBand, setAgeBand] = useState("");
  const [difference, setDifference] = useState(null);
  const [communicator, setCommunicator] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [device, setDevice] = useState("generic");
  const [artSound, setArtSound] = useState("");
  const [artPosition, setArtPosition] = useState("");
  const [glpScripts, setGlpScripts] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      const [a, d, c] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/disorders`),
        axios.get(`${API}/communicator-stages`),
      ]);
      setActivities(a.data);
      setDifferences(d.data);
      setCommunicators(c.data);
    })();
  }, []);

  useEffect(() => {
    if (!difference) { setSeverities([]); setSeverity(null); return; }
    (async () => {
      const r = await axios.get(`${API}/severities`, { params: { disorder_id: difference.id } });
      setSeverities(r.data);
      setSeverity(null);
    })();
  }, [difference]);

  const playActs = useMemo(() => {
    const q = activityFilter.trim().toLowerCase();
    return activities.filter((a) => a.category === "play" && (!q || a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)));
  }, [activities, activityFilter]);
  const routineActs = useMemo(() => {
    const q = activityFilter.trim().toLowerCase();
    return activities.filter((a) => a.category === "routine" && (!q || a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)));
  }, [activities, activityFilter]);

  const isSubscribed = !!user?.is_subscribed;
  const PLAY_CAP = isSubscribed ? 3 : 2;
  const ROUTINE_CAP = 1;

  const selectedActivities = useMemo(
    () => activities.filter((a) => selectedActivityIds.includes(a.id)),
    [activities, selectedActivityIds]
  );
  const selectedPlayCount = selectedActivities.filter((a) => a.category === "play").length;
  const selectedRoutineCount = selectedActivities.filter((a) => a.category === "routine").length;

  const isSelected = (a) => selectedActivityIds.includes(a.id);
  const isCapped = (a) => {
    if (isSelected(a)) return false;
    if (a.category === "play") return selectedPlayCount >= PLAY_CAP;
    if (a.category === "routine") return selectedRoutineCount >= ROUTINE_CAP;
    return false;
  };
  const toggleActivity = (a) => {
    if (isSelected(a)) {
      setSelectedActivityIds((prev) => prev.filter((id) => id !== a.id));
      return;
    }
    if (a.category === "play" && selectedPlayCount >= PLAY_CAP) {
      toast(isSubscribed
        ? `Up to ${PLAY_CAP} play activities allowed.`
        : `Free users can select up to ${PLAY_CAP} play activities. Subscribe to unlock 3.`);
      return;
    }
    if (a.category === "routine" && selectedRoutineCount >= ROUTINE_CAP) {
      toast(`Only 1 routine can be combined per generation.`);
      return;
    }
    setSelectedActivityIds((prev) => [...prev, a.id]);
  };

  const activityChosen = useCustom ? customActivity.trim().length > 1 : selectedActivityIds.length > 0;
  const step = !activityChosen ? 1
    : !ageBand ? 2
    : !difference ? 3
    : !communicator ? 4
    : !severity ? 5
    : 6;

  const generate = async () => {
    if (!activityChosen || !ageBand || !difference || !communicator || !severity) return;
    if (!user?.is_subscribed && (user?.generations_used || 0) >= 1) {
      toast("You've used your free trial — subscribe to keep generating goals.", { description: "Redirecting to subscription plans…" });
      setTimeout(() => nav("/plans"), 900);
      return;
    }
    setGenerating(true);
    try {
      const token = await ensureAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      const body = {
        disorder_id: difference.id,
        communicator_stage_id: communicator.id,
        severity_id: severity.id,
        device,
        user_age_band: ageBand,
      };
      if (useCustom) body.custom_activity_name = customActivity.trim();
      else body.activity_ids = selectedActivityIds;
      if (isArticulationDifference(difference) && artSound.trim() && artPosition) {
        body.articulation_target_sound = artSound.trim();
        body.articulation_position = artPosition;
      }
      if (isGlpDifference(difference) && glpScripts.trim()) {
        body.glp_existing_scripts = glpScripts.trim();
      }
      const res = await axios.post(`${API}/generate-goals`, body, { headers });
      toast.success(res.data.is_trial ? "Sample goal bank ready — subscribe to unlock all 12" : "Goal bank ready");
      await refreshMe();
      nav(`/goal-results/${res.data.id}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast(err.response.data?.detail || "Subscribe to continue using CommPass.");
        nav("/plans");
      } else {
        toast.error(err?.response?.data?.detail || "Goal generation failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="goals-page">
      <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Goal Bank</span>
      <h1 className="font-display text-4xl font-semibold text-[#2C4C3B] mt-1">Build a SMART goal bank</h1>
      <p className="text-[#52606D] mt-2 max-w-2xl">Six steps generate goals across the four AAC competency domains: Linguistic, Social, Strategic and Operational (Janice Light, 1989).</p>

      <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 p-4 rounded-2xl bg-[#F4F5F0] border border-[#E2E8F0]" data-testid="goals-stepper">
        <Step n={1} title="Activity" active={step === 1} done={activityChosen} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={2} title="Age band" active={step === 2} done={!!ageBand} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={3} title="Differences" active={step === 3} done={!!difference} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={4} title="Communicator" active={step === 4} done={!!communicator} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={5} title="Severity" active={step === 5} done={!!severity} />
        <ChevronRight className="w-4 h-4 text-[#A8BAA3]" />
        <Step n={6} title="Device" active={step === 6} done={false} />
      </div>

      {/* Step 1: Activity */}
      <section className="mt-12" data-testid="goals-step-activity">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl text-[#2C4C3B]">1. Select or type an activity</h2>
          <button onClick={() => { setUseCustom(!useCustom); setSelectedActivityIds([]); }}
            className="text-sm text-[#2C4C3B] hover:underline" data-testid="goals-toggle-custom">
            {useCustom ? "← Browse catalog" : "Type your own →"}
          </button>
        </div>
        {useCustom ? (
          <div className="p-6 rounded-2xl bg-white border border-[#E2E8F0]">
            <Input value={customActivity} onChange={(e) => setCustomActivity(e.target.value)}
              placeholder="e.g., Café outing, Birthday party, Park visit…"
              className="rounded-xl" data-testid="goals-custom-activity-input" />
          </div>
        ) : (
          <>
            <div className="relative mb-6 max-w-md" data-testid="goals-activity-filter-wrap">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52606D]" />
              <Input
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                placeholder="Search activities…"
                className="pl-9 rounded-full bg-white border-[#E2E8F0]"
                data-testid="goals-activity-filter-input"
              />
            </div>
            {playActs.length === 0 && routineActs.length === 0 && (
              <div className="p-6 rounded-2xl border border-dashed border-[#E2E8F0] text-center text-[#52606D]" data-testid="goals-activity-empty">
                No matches. Try a different search or <button onClick={() => { setUseCustom(true); setCustomActivity(activityFilter); }} className="text-[#2C4C3B] font-medium hover:underline">type it as a custom activity →</button>
              </div>
            )}
            {playActs.length > 0 && (
              <>
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#C66A51] mb-3">Play <span className="text-[#52606D] normal-case tracking-normal">· pick up to {PLAY_CAP}{!isSubscribed && " — subscribe to unlock 3"} · combine with up to 1 routine</span></h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {playActs.map((a) => {
                    const capped = isCapped(a);
                    return (
                      <button key={a.id} onClick={() => toggleActivity(a)}
                        className={`text-left p-5 rounded-2xl border card-lift ${isSelected(a) ? "border-[#2C4C3B] bg-[#E6F0E9]" : capped ? "border-[#E2E8F0] bg-[#F4F5F0] opacity-50 cursor-not-allowed" : "border-[#E2E8F0] bg-white"}`}
                        data-testid={`goals-activity-${a.slug}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-display text-lg text-[#2C4C3B]">{a.name}</div>
                          {isSelected(a) && <Check className="w-4 h-4 text-[#2C4C3B]" />}
                        </div>
                        <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{a.description}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {routineActs.length > 0 && (
              <>
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#C66A51] mt-10 mb-3">Routines <span className="text-[#52606D] normal-case tracking-normal">· pick up to {ROUTINE_CAP}</span></h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {routineActs.map((a) => {
                    const capped = isCapped(a);
                    return (
                      <button key={a.id} onClick={() => toggleActivity(a)}
                        className={`text-left p-5 rounded-2xl border card-lift ${isSelected(a) ? "border-[#2C4C3B] bg-[#E6F0E9]" : capped ? "border-[#E2E8F0] bg-[#F4F5F0] opacity-50 cursor-not-allowed" : "border-[#E2E8F0] bg-white"}`}
                        data-testid={`goals-activity-${a.slug}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-display text-lg text-[#2C4C3B]">{a.name}</div>
                          {isSelected(a) && <Check className="w-4 h-4 text-[#2C4C3B]" />}
                        </div>
                        <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{a.description}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* Step 2: Age Band */}
      {activityChosen && (
        <section className="mt-12" data-testid="goals-step-age-band">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-2">2. Age band</h2>
          <p className="text-sm text-[#52606D] mb-4">Goal register adjusts to age — adult outputs are autonomy-led and never infantilising.</p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
            {[
              { code: "child", title: "Child", desc: "Play- and routine-anchored goals." },
              { code: "adult", title: "Adult", desc: "Vocation, autonomy and community goals." },
            ].map((opt) => (
              <button key={opt.code} onClick={() => setAgeBand(opt.code)}
                className={`text-left p-5 rounded-2xl border card-lift ${ageBand === opt.code ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`goals-age-band-option-${opt.code}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{opt.title}</div>
                <div className="text-xs text-[#52606D] mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Step 3: Differences */}
      {activityChosen && ageBand && (
        <section className="mt-12" data-testid="goals-step-differences">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">3. Differences / profile</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {differences.filter((d) => !d.coming_soon).map((d) => (
              <button key={d.id} onClick={() => { setDifference(d); setCommunicator(null); }}
                className={`text-left p-5 rounded-2xl border card-lift ${difference?.id === d.id ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`goals-difference-${d.slug}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{d.name}</div>
                <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{d.description}</div>
              </button>
            ))}
          </div>

          {differences.some((d) => d.coming_soon) && (
            <div className="mt-10" data-testid="goals-coming-soon-section">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#C66A51] mb-3">
                Adding soon <span className="text-[#52606D] normal-case tracking-normal">· these profiles are coming in an upcoming release</span>
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {differences.filter((d) => d.coming_soon).map((d) => (
                  <div key={d.id}
                    className="relative text-left p-5 rounded-2xl border border-dashed border-[#E2E8F0] bg-[#F4F5F0] opacity-70 cursor-not-allowed select-none"
                    data-testid={`goals-difference-coming-soon-${d.slug}`}
                    aria-disabled="true">
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] tracking-[0.15em] uppercase bg-[#C66A51]/10 text-[#C66A51] font-semibold">Adding soon</span>
                    <div className="font-display text-lg text-[#52606D] pr-20">{d.name}</div>
                    <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{d.description}</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm text-[#52606D] italic" data-testid="goals-more-differences-line">
                More differences profiles are being added regularly.
              </p>
            </div>
          )}

          {isArticulationDifference(difference) && (
            <div className="mt-6 p-6 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30" data-testid="goals-articulation-fields">
              <h3 className="font-display text-lg text-[#2C4C3B]">Articulation — target sound (optional)</h3>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <Input value={artSound} onChange={(e) => setArtSound(e.target.value)} placeholder="/s/, /k/, /r/…" className="rounded-xl" data-testid="goals-articulation-sound-input" />
                <select value={artPosition} onChange={(e) => setArtPosition(e.target.value)}
                  className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm text-[#1A202C] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                  data-testid="goals-articulation-position-select">
                  <option value="">— Position —</option>
                  <option value="initial">Initial</option>
                  <option value="medial">Medial</option>
                  <option value="final">Final</option>
                </select>
              </div>
            </div>
          )}
          {isGlpDifference(difference) && (
            <div className="mt-6 p-6 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30" data-testid="goals-glp-fields">
              <h3 className="font-display text-lg text-[#2C4C3B]">GLP — existing scripts (optional)</h3>
              <textarea value={glpScripts} onChange={(e) => setGlpScripts(e.target.value)} rows={3}
                placeholder="One script per line…"
                className="mt-3 w-full rounded-xl border border-[#E2E8F0] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                data-testid="goals-glp-scripts-input" />
            </div>
          )}
        </section>
      )}

      {/* Step 4: Communicator */}
      {activityChosen && ageBand && difference && (
        <section className="mt-12" data-testid="goals-step-communicator">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">4. AAC communicator stage</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {communicators.map((c) => (
              <button key={c.id} onClick={() => setCommunicator(c)}
                className={`text-left p-5 rounded-2xl border card-lift ${communicator?.id === c.id ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`goals-communicator-${c.code}`}>
                <span className="text-xs tracking-[0.15em] uppercase text-[#C66A51]">Stage {c.order}</span>
                <div className="font-display text-lg text-[#2C4C3B] mt-1">{c.title}</div>
                <div className="text-xs text-[#52606D] mt-1 line-clamp-2">{c.description}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Step 5: Severity */}
      {activityChosen && ageBand && difference && communicator && (
        <section className="mt-12" data-testid="goals-step-severity">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">5. Severity / stage</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {severities.map((s) => (
              <button key={s.id} onClick={() => setSeverity(s)}
                className={`text-left p-5 rounded-2xl border card-lift ${severity?.id === s.id ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`goals-severity-${s.code}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{s.label}</div>
                <div className="text-xs text-[#52606D] mt-1">{s.description}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Step 6: Device */}
      {activityChosen && ageBand && difference && communicator && severity && (
        <section className="mt-12" data-testid="goals-step-device">
          <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">6. Device</h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
            {[
              { code: "generic", title: "Generic AAC", desc: "Device-agnostic goals." },
              { code: "avaz", title: "Avaz AAC", desc: "Goals reference Avaz features (Quick Folder, Alert button, Core Words)." },
            ].map((opt) => (
              <button key={opt.code} onClick={() => setDevice(opt.code)}
                className={`text-left p-5 rounded-2xl border card-lift ${device === opt.code ? "border-[#2C4C3B] bg-[#E6F0E9]" : "border-[#E2E8F0] bg-white"}`}
                data-testid={`goals-device-${opt.code}`}>
                <div className="font-display text-lg text-[#2C4C3B]">{opt.title}</div>
                <div className="text-xs text-[#52606D] mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>

          <div className="mt-10 p-8 rounded-2xl bg-[#2C4C3B] text-white flex flex-wrap items-center justify-between gap-4" data-testid="goals-generate-panel">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase opacity-70">Ready</div>
              <div className="font-display text-xl mt-1">
                {useCustom ? customActivity : selectedActivities.map((a) => a.name).join(" + ")} · {difference.name}
              </div>
              <div className="text-sm opacity-80 mt-1">
                {ageBand === "adult" ? "Adult" : "Child"} · {communicator.title} · {severity.label} · {device === "avaz" ? "Avaz" : "Generic AAC"}
              </div>
            </div>
            <Button onClick={generate} disabled={generating}
              className="rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white px-7 py-6" data-testid="goals-generate-btn">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating…</> : <><Target className="w-4 h-4 mr-2" /> Build Goal Bank</>}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
